// src/views/Dieta/SeleccionarMateria/SeleccionMateria.js
import React, { useEffect, useState } from "react";
import "./SeleccionMateria.css";
import MateriaTemporal from "./MateriaTemporal";

import {
    cargarClasificaciones,
    buscarMaterias,
    cargarDetalleMateria,
} from "../../../controllers/MateriasController";

import { pickDetalleKeys } from "../../../models/MateriasModel";
import { message } from "../../Mensaje/Mensaje";

// Helpers de sesión/localStorage (compatibles con el modal y el paso 5)
const SESSION_TEMP_KEY = "np_materias_temporales_session_v1";
const LEGACY_TEMP_KEY = "np_temporales"; // donde guarda el modal:contentReference[oaicite:7]{index=7}
const SESSION_SEL_KEY = "np_materias_seleccionadas_v1";

function readTemporalesFromSession() {
    try {
        const a = JSON.parse(sessionStorage.getItem(SESSION_TEMP_KEY) || "[]");
        if (Array.isArray(a) && a.length) return a;
    } catch { }
    try {
        const b = JSON.parse(sessionStorage.getItem(LEGACY_TEMP_KEY) || "[]");
        return Array.isArray(b) ? b : [];
    } catch { }
    return [];
}
function writeTemporalesToSession(arr) {
    try { sessionStorage.setItem(SESSION_TEMP_KEY, JSON.stringify(arr || [])); } catch { }
    try { sessionStorage.setItem(LEGACY_TEMP_KEY, JSON.stringify(arr || [])); } catch { }
}
function readSeleccionadasFromSession() {
    try { const a = JSON.parse(sessionStorage.getItem(SESSION_SEL_KEY) || "[]"); return Array.isArray(a) ? a : []; }
    catch { return []; }
}
function writeSeleccionadasToSession(arr) {
    try { sessionStorage.setItem(SESSION_SEL_KEY, JSON.stringify(arr || [])); } catch { }
}
function readTempDetallesLS() {
    try { return JSON.parse(localStorage.getItem("np_temp_detalles") || "{}"); } catch { return {}; }
}
function writeTempDetallesLS(map) {
    try { localStorage.setItem("np_temp_detalles", JSON.stringify(map || {})); } catch { }
}

export default function SeleccionMateria({ DietasController, onAtras, onSiguiente }) {
    // --------- Estado UI / filtros ---------
    const [q, setQ] = useState("");
    const [clasificacionId, setClasificacionId] = useState("");

    // Catálogo de clasificaciones
    const [clasificaciones, setClasificaciones] = useState([]);

    // Banco de materias filtrado
    const [materias, setMaterias] = useState([]);
    const [loading, setLoading] = useState(false);

    // Selección local del paso
    const [seleccionadas, setSeleccionadas] = useState([]);

    // Temporales (panel izquierdo)
    const [temporales, setTemporales] = useState([]);

    // Modal de creación temporal
    const [modalOpen, setModalOpen] = useState(false);

    // --------- Bootstrap (snapshot + catálogo + banco completo) ---------
    useEffect(() => {
        let alive = true;
        (async () => {
            // recuperar selección previa
            const snap = DietasController?.snapshot?.() || {};
            if (Array.isArray(snap.materiasSeleccionadas) && snap.materiasSeleccionadas.length > 0) {
                setSeleccionadas(snap.materiasSeleccionadas);
            } else {
                setSeleccionadas(readSeleccionadasFromSession());
            }

            // cargar temporales guardados
            setTemporales(
                readTemporalesFromSession().map(r => ({
                    id: String(r.id),
                    nombre: String(r.nombre),
                    clasificacion: "Temporal",
                    temporal: true,
                }))
            );

            // catálogo + banco
            try {
                setLoading(true);
                const [cls, mats] = await Promise.all([
                    cargarClasificaciones(),
                    buscarMaterias({ q: "", clasificacionId: "" }),
                ]);
                if (!alive) return;
                setClasificaciones(Array.isArray(cls) ? cls : []);
                setMaterias(Array.isArray(mats) ? mats : []);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --------- Re-carga por filtros ---------
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                const mats = await buscarMaterias({ q, clasificacionId });
                if (!alive) return;
                setMaterias(Array.isArray(mats) ? mats : []);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [q, clasificacionId]);

    // --------- Helpers selección ---------
    const isSelected = (id) => seleccionadas.some((x) => String(x.id) === String(id));

    const toggleSelBanco = (m) => {
        setSeleccionadas((prev) => {
            const exists = prev.some((x) => String(x.id) === String(m.id));
            const next = exists
                ? prev.filter((x) => String(x.id) !== String(m.id))
                : [...prev, { id: String(m.id), nombre: m.nombre, clasificacion: m.clasificacion ?? "", temporal: !!m.temporal }];

            writeSeleccionadasToSession(next);
            DietasController?.setSeleccionadas?.(next);
            return next;
        });
    };

    const toggleSelTemporal = (idRaw) => {
        const id = String(idRaw);
        const temp = temporales.find(t => String(t.id) === id);
        if (!temp) return;

        setSeleccionadas((prev) => {
            const exists = prev.some((x) => String(x.id) === id);
            const next = exists
                ? prev.filter((x) => String(x.id) !== id)
                : [...prev, { id, nombre: temp.nombre, clasificacion: "Temporal", temporal: true }];

            writeSeleccionadasToSession(next);
            DietasController?.setSeleccionadas?.(next);

            // Asegurar detalle global para el temporal
            const map = readTempDetallesLS();
            const det = map[id];
            if (det && DietasController?.setDetalleMateria) {
                DietasController.setDetalleMateria(id, det);
            }
            return next;
        });
    };

    const vaciarSeleccion = () => {
        setSeleccionadas([]);
        writeSeleccionadasToSession([]);
        DietasController?.setSeleccionadas?.([]);
    };

    // --------- Confirmar (guarda + prefetch detalles) ---------
    const confirmarSeleccion = async () => {
        if (seleccionadas.length === 0) {
            await message.alert({
                title: "Sin selección",
                text: "Selecciona al menos una materia prima antes de confirmar.",
                buttonText: "Entendido",
            });
            return;
        }

        // 1) Persistir selección
        DietasController?.setSeleccionadas(seleccionadas);
        writeSeleccionadasToSession(seleccionadas);

        // 2) Prefetch detalles bromatológicos de BANCO
        for (const m of seleccionadas) {
            try {
                if (m.temporal) continue; // temporales ya van por LS
                const det = await cargarDetalleMateria(m.id);
                const detalle = det?.detalle || det?.bromatologia || det?.composicion || null;
                if (detalle && DietasController?.setDetalleMateria) {
                    DietasController.setDetalleMateria(m.id, detalle);
                }
            } catch {
                // ignorar error individual
            }
        }

        // 3) Toast de éxito
        message.success("Selección guardada correctamente.", "Hecho");
    };

    // --------- Manejo del onGuardar del modal (ALTA de temporal) ---------
    const onGuardarTemporal = (payload /* materia temporal completa */) => {
        const id = String(payload.id);

        // 1) Persistir en sessionStorage (ambas claves)
        const prevTemps = readTemporalesFromSession();
        const exists = prevTemps.some(t => String(t.id) === id);
        const nextTemps = exists ? prevTemps : [...prevTemps, { id, nombre: payload.nombre }];
        writeTemporalesToSession(nextTemps);

        // Refrescar panel de temporales
        setTemporales(
            nextTemps.map(r => ({ id: String(r.id), nombre: String(r.nombre), clasificacion: "Temporal", temporal: true }))
        );

        // 2) Guardar detalles del temporal en localStorage para Paso 5:contentReference[oaicite:8]{index=8}
        const detalles = pickDetalleKeys(payload); // MS, EM, NDT, PB, ...
        const map = readTempDetallesLS();
        map[id] = detalles;
        writeTempDetallesLS(map);

        // 3) Seleccionarlo inmediatamente en la UI y estado global
        setSeleccionadas((prev) => {
            if (prev.some(x => String(x.id) === id)) return prev;
            const next = [...prev, { id, nombre: payload.nombre, clasificacion: "Temporal", temporal: true }];
            writeSeleccionadasToSession(next);
            DietasController?.setSeleccionadas?.(next);
            return next;
        });

        // 4) Exponer detalle al estado global
        if (DietasController?.setDetalleMateria) {
            DietasController.setDetalleMateria(id, detalles);
        }

        message.success("Materia temporal guardada y seleccionada.", "Hecho");
        setModalOpen(false);
    };

    // --------- Derivados UI ---------
    const bancoVacio = !loading && materias.length === 0;
    const selVacia = seleccionadas.length === 0;

    const pillSeleccionada = (m) =>
        <span className={`pill ${m.temporal ? "" : "neutral"}`}>{m.temporal ? "Temporal" : (m.clasificacion || "—")}</span>;

    const pillBanco = (texto) => <span className="pill">{texto || "—"}</span>;

    // --------- JSX ---------
    return (
        <div className="selmat-grid">
            {/* Toolbar superior */}
            <div className="selmat-toolbar">
                <div className="selmat-search">
                    <input
                        placeholder="Buscar materia prima…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    <select
                        className="selmat-select"
                        value={clasificacionId}
                        onChange={(e) => setClasificacionId(e.target.value)}
                    >
                        <option value="">Todas las clasificaciones</option>
                        {clasificaciones.map((c) => (
                            <option key={c.id} value={c.id}>{c.nombre}</option>
                        ))}
                    </select>
                </div>

                <div className="selmat-actions">
                    <button className="btn btn-gold" onClick={() => setModalOpen(true)}>
                        Agregar materia prima temporal
                    </button>
                </div>
            </div>

            {/* Panel izquierdo: Seleccionadas */}
            <div className="selmat-panel">
                <h3 className="panel-title">
                    Seleccionadas
                    <span className="line" />
                </h3>

                <div className="scroll-area">
                    <ul className="list">
                        {selVacia && <li className="empty">Aún no has seleccionado materias</li>}
                        {seleccionadas.map((m) => (
                            <li key={m.id} className="row selected">
                                <span className="name">{m.nombre}</span>
                                {pillSeleccionada(m)}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="temp-actions">
                    <button
                        className="btn-danger-outline"
                        onClick={vaciarSeleccion}
                        disabled={selVacia}
                    >
                        Vaciar selección
                    </button>

                    <button
                        className="btn btn-gold"
                        onClick={confirmarSeleccion}
                        disabled={selVacia}
                        title={selVacia ? "Selecciona al menos una materia" : "Guardar selección (no avanza)"}
                    >
                        Confirmar selección
                    </button>
                </div>
            </div>

            {/* Panel derecho: Banco + Temporales */}
            <div className="selmat-panel">
                <h3 className="panel-title">
                    Banco de materias
                    <span className="line" />
                </h3>

                <div className="scroll-area">
                    <ul className="list">
                        <li className="subheader">Temporales</li>
                        {temporales.length === 0 && <li className="empty">No hay temporales creadas</li>}
                        {temporales.map((t) => {
                            const active = isSelected(t.id);
                            return (
                                <li
                                    key={t.id}
                                    className={`row ${active ? "active" : ""}`}
                                    onClick={() => toggleSelTemporal(t.id)}
                                    title={active ? "Quitar de seleccionadas" : "Agregar a seleccionadas"}
                                >
                                    <span className="name">{t.nombre}</span>
                                    <span className="pill">Temporal</span>
                                </li>
                            );
                        })}

                        <li className="subheader">Banco</li>
                        {loading && <li className="empty">Cargando…</li>}
                        {bancoVacio && <li className="empty">Sin resultados</li>}

                        {!loading &&
                            materias.map((m) => {
                                const active = isSelected(m.id);
                                return (
                                    <li
                                        key={m.id}
                                        className={`row ${active ? "active" : ""}`}
                                        onClick={() => toggleSelBanco(m)}
                                        title={active ? "Quitar de seleccionadas" : "Agregar a seleccionadas"}
                                    >
                                        <span className="name">{m.nombre}</span>
                                        {pillBanco(m.clasificacion)}
                                    </li>
                                );
                            })}
                    </ul>
                </div>
            </div>

            {/* Footer navegación */}
            <div className="selmat-footer">
                <button className="btn ghost" onClick={onAtras}>Atrás</button>
                <button
                    className="btn btn-gold"
                    onClick={async () => {
                        const count = (seleccionadas?.length || 0);
                        if (count === 0) {
                            await message.alert({
                                title: "Confirma tu selección",
                                text: "Debes confirmar al menos una materia prima antes de continuar.",
                                buttonText: "Entendido",
                            });
                            return;
                        }
                        onSiguiente?.();
                    }}
                >
                    Siguiente
                </button>
            </div>

            {/* Modal temporales */}
            <MateriaTemporal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onGuardar={onGuardarTemporal}
            />
        </div>
    );
}
